<template>
  <v-card color="white" rounded="lg" outlined>
    <block v-if="loading"></block>
    <v-card-text class="pa-2">
      <v-alert color="warning">
        <div class="text-h6">CIERRE DE OPERACIONES DE MES</div>
        <p>Este proceso cierra las operaciones de un mes.</p>
        <p>
          Una vez cerrado ya no es posible crear, modificar o eliminar partidas.
        </p>
        <p>
          Si desea modificar un mes cerrado, vaya a la opción de
          <span class="font-weight-bold">APERTURA DE OPERACIONES DE MES</span>
          en el menú
          <span class="font-weight-bold"
            >Contabilidad &#187; Apertura y cierre de mes</span
          >
        </p>
      </v-alert>
      <v-row v-if="showClosingJobStatus" class="pb-2">
        <v-col cols="12" class="py-0">
          <v-progress-linear
            v-if="closingJobRunning"
            indeterminate
            color="primary"
            class="mb-2"
          ></v-progress-linear>
          <v-alert
            dense
            outlined
            :type="closingJobAlertType"
            class="mb-0"
          >
            <div class="font-weight-medium">Estado del cierre</div>
            <div>{{ closingJobLabel }}</div>
          </v-alert>
        </v-col>
      </v-row>
      <p class="text-body-1 text-center">
        Seleccione el mes que desea cerrar operaciones
      </p>

      <!-- form -->
      <v-form @submit.prevent="confirmClose">
        <v-row justify="center">
          <v-col cols="12" sm="4" md="3" lg="2">
            <v-select
              v-model="form.month"
              label="Mes"
              :items="months"
              item-text="monthName"
              item-value="month"
              outlined
              dense
              :disabled="loading || closingJobRunning"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4" md="3" lg="2">
            <v-text-field
              v-model="form.year"
              label="Año"
              outlined
              type="number"
              attach
              dense
              clearable
              :disabled="loading || closingJobRunning"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="pb-3" justify="center">
          <v-col cols="12" sm="6" md="5" lg="3">
            <v-btn type="submit" class="btn-save" dark block :disabled="loading || closingJobRunning">
              <div v-if="$vuetify.breakpoint.xs" class="d-flex flex-column">
                <span>Cerrar operaciones</span><br />
                <span>del mes seleccionado</span>
              </div>
              <template v-else>
                Cerrar operaciones del mes seleccionado
              </template>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <!-- confirm dialog -->
    <alert
      v-model="dialog"
      type="confirm"
      title="Confirmar cierre de operaciones"
      :message="`¿Confirma el cierre de operaciones para el mes ${selectedMonth}?`"
      @action="closeMonthOperations"
    ></alert>
    <alert
      v-model="alert.show"
      v-bind="{ ...alert }"
    ></alert>
  </v-card>
</template>

<script>
export default {
  name: "CloseMonthPage",
  data: () => ({
    loading: false,
    closingJobRunning: false,
    closingJobPollingTimer: null,
    closingJobJobId: null,
    closingJobStatus: null,
    dialog: false,
    form: {
      month: 1,
      year: new Date().getFullYear()
    },
    alert: {
      type: "success",
      show: false,
      title: "",
      body: "",
    },
  }),
  computed: {
    months() {
      const months = [];
      for (let i = 0; i < 12; i++) {
        let monthName = this.moment().month(i).format("MMMM");
        monthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        months.push({ month: i + 1, monthName});
      }
      return months;
    },
    selectedMonth( ) {
      return this.months.find( current => current.month === this.form.month ).monthName
    },
    showClosingJobStatus() {
      return this.closingJobStatus !== null;
    },
    closingJobLabel() {
      if (!this.closingJobStatus) {
        return this.closingJobRunning ? "Iniciando cierre de operaciones..." : "";
      }

      const message = this.getClosingJobText(this.closingJobStatus.message);
      const phase = this.getClosingJobText(this.closingJobStatus.phase);
      const status = this.getClosingJobText(this.closingJobStatus.status);
      const visiblePhase = phase && !/^\d+$/.test(phase) ? phase : "";
      const visibleStatus = status && !/^\d+$/.test(status) ? status : "";

      if (message && visiblePhase) {
        return `${message} (${visiblePhase})`;
      }

      if (message) {
        return message;
      }

      if (visiblePhase) {
        return visiblePhase;
      }

      if (visibleStatus) {
        return visibleStatus;
      }

      if (this.isClosingJobCompleted(this.closingJobStatus)) {
        return "El cierre de operaciones se completó correctamente.";
      }

      if (this.isClosingJobFailed(this.closingJobStatus)) {
        return "El cierre de operaciones presentó un error.";
      }

      return this.closingJobRunning ? "Procesando cierre de operaciones..." : "";
    },
    closingJobAlertType() {
      if (!this.closingJobStatus) {
        return "info";
      }

      if (this.isClosingJobFailed(this.closingJobStatus)) {
        return "error";
      }

      if (this.isClosingJobCompleted(this.closingJobStatus)) {
        return "success";
      }

      return "info";
    }
  },
  beforeDestroy() {
    this.clearClosingJobPolling();
  },
  methods: {
    confirmClose() {
      if (this.closingJobRunning) {
        return;
      }

      this.dialog = true;
    },
    getClosingJobText(value) {
      if (value === null || value === undefined) {
        return "";
      }

      return String(value).trim();
    },
    getClosingJobValue(response, fieldNames, fallback = null) {
      const sources = [];

      if (response && response.result) {
        sources.push(response.result);
      }

      if (response && response.data) {
        sources.push(response.data);
      }

      if (response) {
        sources.push(response);
      }

      for (let i = 0; i < sources.length; i += 1) {
        const source = sources[i];

        if (!source) {
          continue;
        }

        for (let j = 0; j < fieldNames.length; j += 1) {
          const fieldName = fieldNames[j];
          const value = source[fieldName];

          if (value !== undefined && value !== null && value !== "") {
            return value;
          }
        }
      }

      return fallback;
    },
    normalizeClosingJobState(response = {}, fallbackJobId = null) {
      return {
        jobId: this.getClosingJobValue(response, ["jobId", "job_id", "jobID"], fallbackJobId),
        success: this.getClosingJobValue(response, ["success"], null),
        status: this.getClosingJobValue(response, ["status"], null),
        phase: this.getClosingJobValue(response, ["phase"], null),
        message: this.getClosingJobValue(response, ["message"], ""),
        summary: this.getClosingJobValue(response, ["summary"], null),
        completedAt: this.getClosingJobValue(response, ["completedAt", "completed_at"], null)
      };
    },
    isClosingJobFailed(job = {}) {
      if (job.success === false) {
        return true;
      }

      const status = this.getClosingJobText(job.status).toUpperCase();
      const phase = this.getClosingJobText(job.phase).toUpperCase();
      const failureTokens = ["FAILED", "FAIL", "ERROR", "CANCELLED", "CANCELED", "ABORTED"];

      return failureTokens.some((token) => status.indexOf(token) !== -1 || phase.indexOf(token) !== -1);
    },
    isClosingJobCompleted(job = {}) {
      if (this.isClosingJobFailed(job)) {
        return false;
      }

      if (job.completedAt) {
        return true;
      }

      const status = this.getClosingJobText(job.status).toUpperCase();
      const phase = this.getClosingJobText(job.phase).toUpperCase();
      const successTokens = ["COMPLETED", "COMPLETE", "SUCCESS", "SUCCEEDED", "DONE", "FINISHED"];

      return successTokens.some((token) => status.indexOf(token) !== -1 || phase.indexOf(token) !== -1);
    },
    clearClosingJobPolling() {
      if (this.closingJobPollingTimer) {
        clearTimeout(this.closingJobPollingTimer);
        this.closingJobPollingTimer = null;
      }
    },
    scheduleClosingJobPoll() {
      this.clearClosingJobPolling();
      this.closingJobPollingTimer = setTimeout(() => {
        this.pollClosingJobStatus();
      }, 5000);
    },
    prepareClosingJobTracking() {
      this.clearClosingJobPolling();
      this.closingJobJobId = null;
      this.closingJobStatus = {
        jobId: null,
        success: null,
        status: "PENDING",
        phase: "INICIANDO",
        message: "Iniciando cierre de operaciones...",
        summary: null,
        completedAt: null
      };
      this.closingJobRunning = true;
    },
    finishClosingJobTracking() {
      this.clearClosingJobPolling();
      this.closingJobRunning = false;
    },
    getClosingJobErrorMessage(error, fallbackMessage = "Hubo un problema al consultar el estado del cierre.") {
      if (!error) {
        return fallbackMessage;
      }

      if (typeof error === "string") {
        return error;
      }

      if (error.message) {
        return error.message;
      }

      if (error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      if (error.data && error.data.message) {
        return error.data.message;
      }

      return fallbackMessage;
    },
    async pollClosingJobStatus() {
      if (!this.closingJobJobId) {
        this.finishClosingJobTracking();
        return;
      }

      try {
        const response = await this.$API.accountingperiod.getClosingBalanceJobStatus(this.closingJobJobId);
        const normalizedState = this.normalizeClosingJobState(response, this.closingJobJobId);

        this.closingJobStatus = {
          ...this.closingJobStatus,
          ...normalizedState
        };
        this.closingJobJobId = this.closingJobStatus.jobId || this.closingJobJobId;

        if (this.isClosingJobFailed(this.closingJobStatus)) {
          this.finishClosingJobTracking();
          this.showAlert(
            "info",
            "Error al intentar Cerrar las Operaciones del Mes",
            this.closingJobStatus.message || "El cierre de operaciones no pudo completarse."
          );
          return;
        }

        if (this.isClosingJobCompleted(this.closingJobStatus)) {
          this.finishClosingJobTracking();
          this.showAlert(
            "success",
            "Se han Cerrado las Operaciones",
            this.closingJobStatus.message || `Se cerraron las operaciones del mes de ${ this.selectedMonth }: `
          );
          return;
        }

        this.scheduleClosingJobPoll();
      } catch (error) {
        this.closingJobStatus = {
          ...this.closingJobStatus,
          status: "FAILED",
          phase: "ERROR",
          message: this.getClosingJobErrorMessage(error, "No fue posible consultar el estado del cierre."),
          completedAt: new Date().toISOString()
        };
        this.finishClosingJobTracking();
        this.showAlert(
          "info",
          "Error al intentar Cerrar las Operaciones del Mes",
          this.closingJobStatus.message
        );
      }
    },
    async closeMonthOperations() {
      this.loading = true;
      try {
        this.dialog = false;

        this.prepareClosingJobTracking();

        const response = await this.$API.accountingperiod.modifyAccountingPeriod( { month: this.form.month, year: this.form.year, is_closed: 1 } )

        const normalizedState = this.normalizeClosingJobState(response);

        this.closingJobStatus = {
          ...this.closingJobStatus,
          ...normalizedState
        };
        this.closingJobJobId = this.closingJobStatus.jobId || this.closingJobJobId;

        if (this.isClosingJobFailed(this.closingJobStatus)) {
          this.finishClosingJobTracking();
          this.showAlert(
            "info",
            "Error al intentar Cerrar las Operaciones del Mes",
            this.closingJobStatus.message || `Hubo un problema al intentar cerrar las operaciones del mes de ${ this.selectedMonth }: `
          );
          return;
        }

        if (this.isClosingJobCompleted(this.closingJobStatus)) {
          this.finishClosingJobTracking();
          this.showAlert(
            "success",
            "Se han Cerrado las Operaciones",
            this.closingJobStatus.message || `Se cerraron las operaciones del mes de ${ this.selectedMonth }: `
          );
          return;
        }

        if (!this.closingJobJobId) {
          this.finishClosingJobTracking();
          this.showAlert(
            "info",
            "Error al intentar Cerrar las Operaciones del Mes",
            "No se recibió el identificador del job de cierre."
          );
          return;
        }

        this.scheduleClosingJobPoll();
      } catch (error) {
        this.closingJobStatus = {
          ...this.closingJobStatus,
          status: "FAILED",
          phase: "ERROR",
          message: this.getClosingJobErrorMessage(error, `Hubo un problema al intentar cerrar las operaciones del mes de ${ this.selectedMonth }: ${ error.message }`),
          completedAt: new Date().toISOString()
        };
        this.finishClosingJobTracking();
        this.showAlert(
          "info",
          `Error al intentar Cerrar las Operaciones del Mes`,
          this.closingJobStatus.message
        );
      } finally { 
        this.loading = false;
      }
    },
    showAlert(type, title, message) {
      type = type == null ? "info" : type;
      this.alert.type = type;
      this.alert.title = title;
      this.alert.message = message;
      this.alert.show = true;
    },
    // closeMonthOperations() {
    //   this.dialog = false;
    //   console.log("close month operations", this.form.month);
    // },
  },
};
</script>
